import React, { useState, useContext, useCallback, useEffect, useMemo } from "react";
import { Box, Tab, Tabs, Tooltip, useTheme } from '@mui/material';
import i18next from "i18next";
import _ from "lodash";

import useStyles from "style/js-style/containers/administration/roles/privileges/PrivilegesPanelStyle";
import { Context } from "states/Store";
import { SUCCESS, ERROR, Operator } from "services/common/Constants"
import EATabPanel from "components/common/EATabPanel";
import EAIcon from "components/common/EAIcon";
import ConfigurationDrop from "containers/administration/roles/privileges/common/ConfigurationDrop";
import ConfigurationLine from "containers/administration/roles/privileges/common/ConfigurationLine";
import {
  getPrivilegeAscendants,
  getPrivilegeDescendants,
  RolePrivilegesEditorContext,
  isEventPrivilege,
  THIRD_PARTY_ATTRIBUTE_MANAGEMENT_ACTIONS,
  DOSSIER_ATTRIBUTE_MANAGEMENT_ACTIONS,
  RULE_MANAGEMENT_ACTIONS,
  ROLE_MANAGEMENT_ACTIONS,
  USER_MANAGEMENT_ACTIONS,
  DOSSIER_EVENT_MANAGEMENT,
  DOCUMENT_EVENT_MANAGEMENT,
  THIRD_PARTY_MANAGEMENT_ACTIONS,
  DOSSIER_MANAGEMENT_ACTIONS,
  DOCUMENT_MANAGEMENT_ACTIONS,
  CONTACT_MANAGEMENT_ACTIONS,
  PrivilegeEnum,
  RoleBasedAccessControlContext,
  INDUED_MANAGEMENT_ACTIONS,
  CALCULATION_MANAGEMENT_ACTIONS,
  TRANSPARENCY_MANAGEMENT_ACTIONS,
  FINANCIAL_STRENGTH_MANAGEMENT_ACTIONS,
  DNB_ESG_MANAGEMENT_ACTIONS,
  OWLIN_MANAGEMENT_ACTIONS,
  TIERS_EVENT_MANAGEMENT,
  isEventTaskPrivilege,
  EVENT_CHECK_INDETERMINED,
  ECOVADIS_MANAGEMENT_ACTIONS,
  SOURCE_TAB_OPTIONS,
  DATA_HUB_MANAGEMENT_ACTIONS,
  THEME_MANAGEMENT_ACTIONS,
  CONTEXT_MANAGEMENT_ACTIONS,
  SECURITY_RATING_MANAGEMENT_ACTIONS,
  PROPERTY_PRIVILEGES,
  EXPORTS_MANAGEMENT_ACTIONS,
  CREDITSAFE_MANAGEMENT_ACTIONS,
  IMPORTS_AND_UPDATES_MANAGEMENT_ACTIONS,
  REQUEST_MANAGEMENT_ACTIONS,
  CONTEXT_MODEL_MANAGEMENT_ACTIONS,
  PARTNER_ATTRIBUTE_MANAGEMENT_ACTIONS
} from 'services/common/RolesUtils';
import {
  updateRolePrivileges,
  getRolePrivileges,
} from "services/edge/RoleService";
import { getAllReports } from "services/edge/ReportService";
import EAButton from "components/common/EAButton";
import RoleScopes from "./common/RoleScopes";
import EventPrivilegeBlock from "./common/EventPrivilegeBlock";
import { hasOption } from "services/common/Utils";
import RoleSaveDialog from '../dialogs/RoleSaveDialog';
import EAColorIcon from '../../../../components/common/EAColorIcon';
import { getDataHubSources } from "services/common/DataHubUtils";

const PrivilegesPanel = ({ accountActivities, accountCountries, accountAttributesAndIndicators, role, isSelectedRoleReadOnly, onValidateSuccess = () => { }, qualifyingCriteriaSelectedrole = [] }) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [rolePrivileges, setRolePrivileges] = useState(role ? role.flatPrivileges : []);
  const [baseRolePrivileges, setBaseRolePrivileges] = useState([]);
  const [submitHandler, setSubmitHandler] = useState(null);
  const [isSavingDialogOpen, setIsSavingDialogOpen] = useState(false);
  const [disabledSourceTab, setDisabledSourceTab] = useState(false);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const theme = useTheme();
  const [accountReports, setAccountReports] = useState([]);

  // All criteria available for properties for a given privilege are listed in this list :
  const [listAllPropertiesCriteria, setListAllPropertiesCriteria] = useState([]);

  const {thirdpartyAttributes, dossierAttributes,
    thirdpartyCalculatedAttributes, dossierCalculatedAttributes,
  thirdpartyIndicators, dossierIndicators} = accountAttributesAndIndicators;

  useEffect(() => {
      // Initialization of all criteria available for properties
      const initialList = [];

      // For Indicators
      let thirdparyIndicatorsAllPropertiesCriteria = {}
      thirdpartyIndicators?.forEach(indicator => {
        thirdparyIndicatorsAllPropertiesCriteria[indicator?.value] = indicator.options.map(optionsBranche => {
            return {operator: Operator.EQUAL, value: optionsBranche?.value}
          });
        })

      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED,
        propertiesCriteria: thirdparyIndicatorsAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR,
        propertiesCriteria: thirdparyIndicatorsAllPropertiesCriteria
      })

      let dossierIndicatorsAllPropertiesCriteria = {}
      dossierIndicators?.forEach(indicator => {
        dossierIndicatorsAllPropertiesCriteria[indicator?.value] = indicator.options.map(optionsBranche => {
            return {operator: Operator.EQUAL, value: optionsBranche?.value}
          });
        })

      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED,
        propertiesCriteria: dossierIndicatorsAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR,
        propertiesCriteria: dossierIndicatorsAllPropertiesCriteria
      })
  
      // For Calculated attributes
      let thirdpartyCalculatedAttributesAllPropertiesCriteria = {}
      thirdpartyCalculatedAttributes?.forEach(calculatedAttribute => {
        thirdpartyCalculatedAttributesAllPropertiesCriteria[calculatedAttribute?.value] = calculatedAttribute.options.map(optionsBranche => {
          return {operator: Operator.EQUAL, value: optionsBranche?.value}
        });
      })

      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED,
        propertiesCriteria: thirdpartyCalculatedAttributesAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE,
        propertiesCriteria: thirdpartyCalculatedAttributesAllPropertiesCriteria
      })

      let dossierCalculatedAttributesAllPropertiesCriteria = {}
      dossierCalculatedAttributes?.forEach(calculatedAttribute => {
        dossierCalculatedAttributesAllPropertiesCriteria[calculatedAttribute?.value] = calculatedAttribute.options.map(optionsBranche => {
          return {operator: Operator.EQUAL, value: optionsBranche?.value}
        });
      })

      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED,
        propertiesCriteria: dossierCalculatedAttributesAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE,
        propertiesCriteria: dossierCalculatedAttributesAllPropertiesCriteria
      })
    
      // For Attributes third-party
      let attributesThirdpartyAllPropertiesCriteria = {}
      thirdpartyAttributes?.forEach(attribute => {
        attributesThirdpartyAllPropertiesCriteria[attribute?.id] = attribute.options ? attribute.options?.map(option => {
          return {operator: Operator.EQUAL, value: option?.value}
        }) : [];
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED,
        propertiesCriteria: attributesThirdpartyAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE,
        propertiesCriteria: attributesThirdpartyAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE,
        propertiesCriteria: attributesThirdpartyAllPropertiesCriteria
      })

      // For Attributes dossier
      let attributesDossierAllPropertiesCriteria = {}
      dossierAttributes?.forEach(attribute => {
        attributesDossierAllPropertiesCriteria[attribute?.id] = attribute.options ? attribute.options?.map(option => {
          return {operator: Operator.EQUAL, value: option?.value}
        }) : [];
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE,
        propertiesCriteria: attributesDossierAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED,
        propertiesCriteria: attributesDossierAllPropertiesCriteria
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE,
        propertiesCriteria: attributesDossierAllPropertiesCriteria
      })

      //For dataHub Sources
      let dataHubSourcesPropertiesCriteria = {}
      getDataHubSources().forEach(source => {
        dataHubSourcesPropertiesCriteria[source?.sourceId] = [];
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED,
        propertiesCriteria: dataHubSourcesPropertiesCriteria
      })

      // For Reports
      let reportsAllPropertiesCriteria = {}
      accountReports?.forEach(report => {
        const reportId = report.assignmentId || report.reportId;
        if (reportId != null) {
          reportsAllPropertiesCriteria[reportId] = [];
        }
      })
      initialList.push({
        privilege: PrivilegeEnum.PRIVILEGE_VIEW_REPORTS,
        propertiesCriteria: reportsAllPropertiesCriteria
      })

      setListAllPropertiesCriteria(initialList);

  }, [dossierAttributes, dossierCalculatedAttributes, dossierIndicators, thirdpartyAttributes, thirdpartyCalculatedAttributes, thirdpartyIndicators, accountReports])


  const hasNecessaryPrivilege = useCallback((privilege, rolePrivileges, propertyId) => {
  //Some privilege (like event privilege) are available only if the role has some other privilege activated
  // ex: PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED need PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE

  if(!privilege?.necessaryPrivileges) {
    return true;
  }

  const result = privilege?.necessaryPrivileges?.map(necessaryPrivilege => {
    const hasAnyOfPrivilegesParentsGranted = [...getPrivilegeAscendants(necessaryPrivilege)].some(asc => 
      rolePrivileges.map(privilege => privilege.name).includes(asc)
    )
    // Verify if the privilege can be specific to a property id
    const isPropertyPrivilege = PROPERTY_PRIVILEGES.some(sp => sp.privilege === necessaryPrivilege.name);

    if(hasAnyOfPrivilegesParentsGranted) {
      //Si le rôle à l'un des privileges parents du correlatedPrivilege, return true
      return true;
    } else if(propertyId != null && isPropertyPrivilege) {
      const currentCorrelatedPrivilege = rolePrivileges?.find(p => p.name === necessaryPrivilege.name) || undefined;
      if(!currentCorrelatedPrivilege) {
        return false;
      } else {
        // vérifier si le privilege contient le propertyId fourni
        const propertiesCriteria = currentCorrelatedPrivilege?.propertiesCriteria || {}
        return propertiesCriteria? Object.keys(propertiesCriteria)?.includes(propertyId.toString()) : false;
      }

    } else {
      return rolePrivileges?.map(priv => priv.name)?.includes(necessaryPrivilege.name)
    }

  })
  return result?.includes(true);

}, [])


  const hasGrantablePrivilegeForProperty = useCallback((privilege, propertyId, criteria) => {
    // Boolean to know if the privilegeId/criteria are grantable from the baseRole
    if(!hasNecessaryPrivilege(privilege, rolePrivileges, propertyId)) {
      return false;
    }
    if (role?.baseRoleId === null) {
      return true;
    }
    if (!_.isEmpty(baseRolePrivileges)) {
      // Special case for reports: if parent has PRIVILEGE_VIEW_REPORTS_ALL, all individual reports are grantable
      if(privilege.name === 'PRIVILEGE_VIEW_REPORTS' &&
         baseRolePrivileges?.some(priv => priv.name === 'PRIVILEGE_VIEW_REPORTS_ALL')) {
        return true;
      }

      if(baseRolePrivileges?.map(priv => priv.name)?.includes(privilege.name)) {
        const grantablePrivilege = baseRolePrivileges?.find(priv => priv.name === privilege.name);
        if(!grantablePrivilege) return false;

        if(criteria && criteria.length > 0) {
          //if criteria is given and criteria != [], we evaluate if all criteria is grantable
          return Object.keys(grantablePrivilege?.propertiesCriteria)?.includes(propertyId.toString()) &&
          criteria.every(item => {
            // criterion is grantable if present in grantablePrivilege's criteria,
            // or grantablePrivilege's criteria for this property are empty
            return grantablePrivilege?.propertiesCriteria[propertyId]?.length === 0 ||
            grantablePrivilege?.propertiesCriteria[propertyId]?.some(criterion => criterion.operator === item.operator &&
              criterion.value === item.value
            );
          })

        } else {
          return Object.keys(grantablePrivilege?.propertiesCriteria)?.includes(propertyId.toString());
        }

      }
    }
    return false;
  }, [baseRolePrivileges, hasNecessaryPrivilege, role?.baseRoleId, rolePrivileges]);

  const getAllGrantablePropertiesCriteria = useCallback((privilege) => {
    // get all existing propertiesCriteria for this privilege :
   const propertiesCriteria = listAllPropertiesCriteria?.find(item => item.privilege?.name === privilege.name)?.propertiesCriteria || {};
   // get all grantablePropertiesCriteria to select for this privilege
   const grantablepropertiesCriteria = {};
   for (const [id, criteria] of Object.entries(propertiesCriteria)) {
     const criteriaGrantable = [];
     criteria?.forEach(criterion => {
       if(hasGrantablePrivilegeForProperty(privilege, id, [criterion])) {
         criteriaGrantable.push(criterion)
       }
     })
     const isPropertyWithNoCriteria = !propertiesCriteria[id] || propertiesCriteria[id].length === 0;
     if(criteriaGrantable.length > 0 ||
      (isPropertyWithNoCriteria && hasGrantablePrivilegeForProperty(privilege, id, null)) ) {
      grantablepropertiesCriteria[id] = criteriaGrantable;
     }
   }
   return grantablepropertiesCriteria;
 }, [hasGrantablePrivilegeForProperty, listAllPropertiesCriteria])

  //for specific events privileges with properties, if global all privilege is present, selected all properties
  const determinePropertiesThatMustBeSelected = useCallback(privileges => {
    const privilegesNames = privileges.map(privilege => privilege.name);
    return privileges.map(privilege => {
      const grantablePropertiesCriteria = getAllGrantablePropertiesCriteria(privilege) ;
      //get current propertiesCriteria
      let allPropertiesCriteria = privilege.propertiesCriteria || {};

      switch(privilege.name) {
        case PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE.name:
        case PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE.name:
          thirdpartyAttributes.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.id] = grantablePropertiesCriteria[att.id] || [];
            } else if(allPropertiesCriteria[att.id]?.length === 0 && grantablePropertiesCriteria[att.id]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.id] = grantablePropertiesCriteria[att.id] || [];
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE.name:
          thirdpartyCalculatedAttributes.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || [];
            } else if(allPropertiesCriteria[att.value]?.length === 0 && grantablePropertiesCriteria[att.value]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || []
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR.name:
          thirdpartyIndicators.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || [];
            } else if(allPropertiesCriteria[att.value]?.length === 0 && grantablePropertiesCriteria[att.value]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || []
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE.name:
        case PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE.name:
          dossierAttributes.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.id] = grantablePropertiesCriteria[att.id] || [];
            } else if(allPropertiesCriteria[att.id]?.length === 0 && grantablePropertiesCriteria[att.id]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.id] = grantablePropertiesCriteria[att.id] || [];
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_VIEW_REPORTS.name:
          // For reports: if PRIVILEGE_VIEW_REPORTS_ALL is present, we don't need PRIVILEGE_VIEW_REPORTS at all
          // Only process individual reports when _ALL is NOT present
          if(!privilegesNames.includes(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL.name)) {
            (accountReports || []).forEach(report => {
              const reportId = report.assignmentId || report.reportId;
              if(reportId != null) {
                if(allPropertiesCriteria[reportId]?.length === 0 && grantablePropertiesCriteria[reportId]?.length > 0){
                  //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
                  allPropertiesCriteria[reportId] = grantablePropertiesCriteria[reportId] || [];
                }
              }
            })
          }
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE.name:
          dossierCalculatedAttributes.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || [];
            } else if(allPropertiesCriteria[att.value]?.length === 0 && grantablePropertiesCriteria[att.value]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || []
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }

        case PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED.name:
        case PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR.name:
          dossierIndicators.forEach(att => {
            if(privilegesNames.includes(privilege.name + "_ALL")) {
              //when _ALL property privilege is present, we selected all properties and all criteria
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || [];
            } else if(allPropertiesCriteria[att.value]?.length === 0 && grantablePropertiesCriteria[att.value]?.length > 0){
              //if privilege is present but with criteria empty and grantable criteria, we assume all criteria should be toggle on
              allPropertiesCriteria[att.value] = grantablePropertiesCriteria[att.value] || []
            }
          })
          return {
            ...privilege,
            propertiesCriteria: allPropertiesCriteria
          }


        default:
          return privilege;
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdpartyCalculatedAttributes, dossierAttributes, thirdpartyIndicators, thirdpartyAttributes, accountReports]);

  const fetchBaseRolePrivileges = useCallback(async (accountId, roleId) => {
    try {
      const resp = await getRolePrivileges(accountId, roleId, null);
      setBaseRolePrivileges(determinePropertiesThatMustBeSelected(resp.content));
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    }
  }, [determinePropertiesThatMustBeSelected, dispatch]);

  const addChildrenForEventPrivileges = useCallback((privileges) => {
    //Si les privileges enfants d'un privilege d'événement sont manquants, on les rajoute manuellement
    const privilegesNames = privileges.map(privilege => privilege.name).filter(name => name.includes("PRIVILEGE_EVENT_"));
    privilegesNames.forEach(privilegeName => {
      const childs = PrivilegeEnum[privilegeName]?.childPrivileges || []
      childs.forEach(child => {
        if(!privileges.some(privilege => privilege.name === child.name)) {
          privileges.push(child);
          addChildrenForEventPrivileges([child]);
        }
      })
    });
    return privileges;

  }, []);



  useEffect(() => {
    setDisabledSourceTab(!SOURCE_TAB_OPTIONS.some(option => hasOption(state.accounts, state.account.id, option)));
  }, [state.accounts, state.account.id]);

  useEffect(() => {
    if (state.account && role) {
      const accountPrivileges = addChildrenForEventPrivileges(determinePropertiesThatMustBeSelected(role.flatPrivileges))
      setRolePrivileges(determinePropertiesThatMustBeSelected(accountPrivileges));
    }
  }, [state.account, role, fetchBaseRolePrivileges, determinePropertiesThatMustBeSelected, addChildrenForEventPrivileges]);

  useEffect(() => {
    if (state.account && role && role.baseRoleId !== null) {
        fetchBaseRolePrivileges(state.account.id, role.baseRoleId);
    }
  }, [state.account, role, fetchBaseRolePrivileges]);

  // Fetch reports for permissions
  useEffect(() => {
    if (state.account?.id) {
      (async function fetchReports() {
        try {
          const reports = await getAllReports(state.account.id, dispatch);
          const reportsList = Array.isArray(reports) ? reports : (reports?.content || []);
          setAccountReports(reportsList);
        } catch (e) {
          console.error("Error fetching reports:", e);
        }
      })();
    }
  }, [state.account?.id, dispatch]);


  const hasCurrentAnyOfPrivileges = useCallback((...privileges) => {
    if (!_.isEmpty(rolePrivileges)) {

      const hasOneNecessaryPrivilege = privileges.some(priv => hasNecessaryPrivilege(priv, rolePrivileges));
      if(!hasOneNecessaryPrivilege) {
        return false;
      }
      return privileges.some(priv => rolePrivileges.map(privilege => privilege.name).includes(priv.name)
        || [...getPrivilegeAscendants(priv)].some(asc => rolePrivileges.map(privilege => privilege.name).includes(asc)));
    }

    return false;
  }, [hasNecessaryPrivilege, rolePrivileges])

  const hasCurrentEventPrivilege = useCallback((privilege) => {

    if(!hasNecessaryPrivilege(privilege, rolePrivileges)) {
      return false;
    }
      
    
    if (_.isEmpty(privilege.childPrivileges)) {
      return rolePrivileges?.map(priv => priv.name)?.includes(privilege.name)
    } else {
      if (privilege.childPrivileges.every(child => hasCurrentEventPrivilege(child) === true)) {
        return true;
      // if one or more children is check, return INDETERMINED
      } else if (privilege.childPrivileges.some(child => hasCurrentEventPrivilege(child) !== false)) {
        return EVENT_CHECK_INDETERMINED;
      }

      return false;
    }

  }, [hasNecessaryPrivilege, rolePrivileges])

  const hasCurrentAnyOfPrivilegesOnResource = useCallback((resourceId, ...locPrivileges) => {
    if (!_.isEmpty(rolePrivileges)) {

      const hasOneNecessaryPrivilege = locPrivileges.some(priv => hasNecessaryPrivilege(priv, rolePrivileges));
      if(!hasOneNecessaryPrivilege) {
        return false;
      }

      return locPrivileges.some(priv => rolePrivileges.map(privilege => privilege.name)?.includes(priv.name) && rolePrivileges.find(privilege => privilege.name === priv.name).resources?.includes(resourceId));
    }
    return false;
  }, [hasNecessaryPrivilege, rolePrivileges])

  const hasCurrentAllOfPrivilegesOnResource = useCallback((resources, ...locPrivileges) => {
    if (!_.isEmpty(rolePrivileges) && resources?.length > 0) {
      return locPrivileges.every(priv => rolePrivileges.map(privilege => privilege.name)?.includes(priv.name) && resources.every(ressource => rolePrivileges.find(privilege => privilege.name === priv.name).resources?.includes(ressource)));
    }
    return false;
  }, [rolePrivileges])

  const hasCurrentAllOfPrivileges = useCallback((...privileges) => {
    return privileges.every(p => hasCurrentAnyOfPrivileges(p));
  }, [hasCurrentAnyOfPrivileges])

  const hasBaseAnyOfPrivileges = useCallback((...privileges) => {

    const hasOneNecessaryPrivilege = privileges.some(priv => hasNecessaryPrivilege(priv, rolePrivileges));
    if(!hasOneNecessaryPrivilege) {
      return false;
    }

    if (role?.baseRoleId === null) {
      return true;
    }

    if (!_.isEmpty(baseRolePrivileges)) {
      return privileges.some(priv => baseRolePrivileges.map(privilege => privilege.name).includes(priv.name) || [...getPrivilegeAscendants(priv)].some(asc => baseRolePrivileges.map(privilege => privilege.name).includes(asc)));
    }

    return false;
  }, [role?.baseRoleId, baseRolePrivileges, rolePrivileges, hasNecessaryPrivilege]);

  const hasBaseAllOfPrivileges = useCallback((...privileges) => {
    return privileges.every(p => hasBaseAnyOfPrivileges(p));
  }, [hasBaseAnyOfPrivileges]);


  const pushIfNotExists = useCallback((prev, name) => {
    const exists = prev.some((priv) => priv.name === name);
    // get all grantablePropertiesCriteria to select for this privilege
    const grantablepropertiesCriteria = getAllGrantablePropertiesCriteria(prev);
    if(!exists) {
      prev.push({ name, resources: [], propertiesCriteria: grantablepropertiesCriteria })
    }
  }, [getAllGrantablePropertiesCriteria])

  const togglePrivileges = useCallback((...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        if (hasCurrentAnyOfPrivileges(privilege)) {
          prev = prev.filter(priv => priv.name !== privilege.name);
          getPrivilegeAscendants(privilege).forEach((asc) => prev = prev.filter(priv => priv.name !== asc));
          if (isEventPrivilege(privilege.name) || isEventTaskPrivilege(privilege.name)) { // For Event Privileges, we remove also child privileges for checkbox purposes
            getPrivilegeDescendants(privilege).forEach((desc) => prev = prev.filter(priv => priv.name !== desc));
          }
        } else {
          prev.push(privilege);
          getPrivilegeDescendants(privilege).forEach((desc) => pushIfNotExists(prev, desc));
        }
      });
      return Array.from(prev);
    })
  }, [hasCurrentAnyOfPrivileges, pushIfNotExists]);

  const selectAllEventPrivileges = (privilege) => {
    // get all grantablePropertiesCriteria to select for this privilege
    const grantablepropertiesCriteria = getAllGrantablePropertiesCriteria(privilege);

    setRolePrivileges(prev => {
        if (hasBaseAnyOfPrivileges(privilege)) {
          if (hasCurrentAnyOfPrivileges(privilege) ) {
            return prev.map(currentprivilege => {
              if (currentprivilege.name === privilege.name) {
                return {
                  ...currentprivilege,
                  // select of all properties grantabled
                  propertiesCriteria: grantablepropertiesCriteria
                }
              } else {
                return currentprivilege;
              }
            })
          } else {
            prev.push({
              ...privilege, 
              propertiesCriteria: grantablepropertiesCriteria
            });
          }
        }
        getPrivilegeDescendants(privilege).forEach((desc) => {
          hasBaseAnyOfPrivileges(PrivilegeEnum[desc]) && pushIfNotExists(prev, desc)
        });
      return Array.from(prev);
    })
  }

  const unselectAllEventPrivileges = (...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        prev = prev.filter(priv => priv.name !== privilege.name);
        getPrivilegeAscendants(privilege).forEach((asc) => prev = prev.filter(priv => priv.name !== asc));
        if (isEventPrivilege(privilege.name)) { // For Event Privileges, we remove also child privileges for checkbox purposes
          getPrivilegeDescendants(privilege).forEach((desc) => prev = prev.filter(priv => priv.name !== desc));
        }
      });
      return Array.from(prev);
    })
  }



  const togglePrivilegesForAllResources = useCallback((resources, ...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        const grantedPrivilege = prev.find(priv => priv.name === privilege.name);
        if (grantedPrivilege) {
          if (!hasCurrentAllOfPrivilegesOnResource(resources, privilege)) {
            grantedPrivilege.resources = resources;
          } else {
            grantedPrivilege.resources = [];
          }
        } else {
          prev.push({ name: privilege.name, resources: resources });
        }
      });
      return Array.from(prev);
    })
  }, [hasCurrentAllOfPrivilegesOnResource, setRolePrivileges]);


  // Toggle ressource (ex: document) of a privilege
  const toggleResourcePrivilege = useCallback((resourceId, ...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        const grantedPrivilege = prev.find(priv => priv.name === privilege.name);
        if (grantedPrivilege) {
          if (hasCurrentAnyOfPrivilegesOnResource(resourceId, privilege)) {
            grantedPrivilege.resources = grantedPrivilege.resources.filter(ressource => ressource !== resourceId);
          } else {
            grantedPrivilege.resources = grantedPrivilege.resources || [];
            grantedPrivilege.resources.push(resourceId);
          }
        } else {
          prev.push({ name: privilege.name, resources: [resourceId] });
        }
      });
      return [...Array.from(prev), ...Array.from(privileges.filter(pr => pr.resources?.length > 0))];
    })
  }, [hasCurrentAnyOfPrivilegesOnResource, setRolePrivileges]);


  /* Manage privileges on properties */



   const hasPrivilegeOnProperty = useCallback((propertyId, criterion, locPrivilege) => {
    // return true if privilege is granted for this propertyId or, if criterion given, this criterion property
    if (!_.isEmpty(rolePrivileges)) {

      if(!hasNecessaryPrivilege(locPrivilege, rolePrivileges)) {
        return false;
      }

      //if one of parent privilege is selected, the property must also be show as selected
      const parentsPrivileges = getPrivilegeAscendants(locPrivilege);
      if(rolePrivileges.some(priv => parentsPrivileges.has(priv.name) && !isEventPrivilege(priv.name)) ) {
        return true;
      }
      if(criterion) {
        const currentPropertiesCriteria = rolePrivileges.find(priv => locPrivilege.name === priv.name)?.propertiesCriteria;
        if(!currentPropertiesCriteria || !currentPropertiesCriteria[propertyId]) {
          return false;
        }
        if(currentPropertiesCriteria[propertyId]?.length === 0) {
          //if propertyId is present but array empty, no criterion available or all crieteria granted
          return true;
        }
        return currentPropertiesCriteria[propertyId].some(currentCriterion => {
          return currentCriterion.operator === criterion.operator && currentCriterion.value === criterion.value
        });
      } else {
        //case where criterion == null
        const allPropertiesCriteria = rolePrivileges.find(priv => locPrivilege.name === priv.name)?.propertiesCriteria;
        const propertyCriteria = allPropertiesCriteria ? allPropertiesCriteria[propertyId] : null

        const hasPropertyIdKey = allPropertiesCriteria ? Object.keys(allPropertiesCriteria)?.includes(propertyId.toString()) : false

        if(!propertyCriteria && hasPropertyIdKey) {
          //no criteria available for this property, is propertyId is present in keys, the property is toogled on
          return true;
        } else if(!propertyCriteria) {
          //no criteria selected or available
          return false;
        } else {
          const grantablepropertiesCriteria = getAllGrantablePropertiesCriteria(locPrivilege)
          if(grantablepropertiesCriteria && grantablepropertiesCriteria[propertyId]) {
            const grantableCriteria = grantablepropertiesCriteria[propertyId]
            if(propertyCriteria.length === grantableCriteria?.length) {
              // all criteria are selected
              return true;
            } else {
              //only partial criteria are selected
              return EVENT_CHECK_INDETERMINED;
            }
          } else {
            //without grantablepropertiesCriteria but with propertyId in propertiesCriteria, this propertyId should be toogle on
            return true;
          }
        }
      }
    }
    return false;
  }, [getAllGrantablePropertiesCriteria, hasNecessaryPrivilege, rolePrivileges])

  const hasCurrentPrivilegeOnAllProperties = useCallback((locPrivilege) => {

    const propertiesCriteria = listAllPropertiesCriteria?.find(item => item.privilege?.name === locPrivilege.name)?.propertiesCriteria || {};
    const propertiesIds = Object.keys(propertiesCriteria) || [];

    if (!_.isEmpty(rolePrivileges) && rolePrivileges.map(privilege => privilege.name)?.includes(locPrivilege.name)) {
      //If not properties available, return true;
      if(propertiesIds.length === 0) {
        return true;
      }
      //If all properties is check, return true;
      if(propertiesIds.every(id => {
        return hasPrivilegeOnProperty(id, null, locPrivilege) === true;
      })) {
        return true;
      // If one property or more are check, return INDETERMINED
      } else if(propertiesIds.some(id => {
        const hasCurrentProperty = hasPrivilegeOnProperty(id, null, locPrivilege);
        return hasCurrentProperty === EVENT_CHECK_INDETERMINED || hasCurrentProperty === true;
      })) {
        return EVENT_CHECK_INDETERMINED
      }
    }
    return false;
  }, [hasPrivilegeOnProperty, listAllPropertiesCriteria, rolePrivileges])

  const togglePrivilegeOnAllProperties = useCallback((privilege) => {
    // get all grantablePropertiesCriteria to select for this privilege
    const grantablepropertiesCriteria = getAllGrantablePropertiesCriteria(privilege);

    setRolePrivileges(prev => {
        const grantedPrivilege = prev.find(priv => priv.name === privilege.name);
        if (grantedPrivilege) {
          if (hasCurrentPrivilegeOnAllProperties(privilege) === false) {
            let propertiesCriteria = {};
            Object.keys(grantablepropertiesCriteria).forEach(element => {
              propertiesCriteria[element] = grantablepropertiesCriteria[element] || [];
            });
            grantedPrivilege.propertiesCriteria = propertiesCriteria;
          } else {
            // when all properties is toggle off, remove "_ALL" privilege property if present
            prev = prev.filter(pri => pri.name !== (privilege.name + "_ALL"));

            return Array.from(prev.filter(pr => pr.name !== grantedPrivilege.name));
          }
        } else {
          prev.push({ name: privilege.name, propertiesCriteria: grantablepropertiesCriteria });
        }
      return Array.from(prev);
    })
  }, [getAllGrantablePropertiesCriteria, hasCurrentPrivilegeOnAllProperties]);

  const togglePropertyPrivilege = useCallback((propertyId, propertyCriterion, privilegeAllProperties, ...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        const grantedPrivilege = prev.find(priv => priv.name === privilege.name);

        // get all grantablePropertiesCriteria to select for this privilege
        const grantablepropertiesCriteria = getAllGrantablePropertiesCriteria(privilege);

        if (grantedPrivilege) {
                const hasPropertyToogledOn = hasPrivilegeOnProperty(propertyId, propertyCriterion, privilege);
          if (hasPropertyToogledOn === true) {
            // Property (and criterion) should be toggled off
            if(propertyCriterion && grantedPrivilege.propertiesCriteria[propertyId]?.length > 1) {
              grantedPrivilege.propertiesCriteria[propertyId] = grantedPrivilege.propertiesCriteria[propertyId]?.filter(criterion =>
                !(criterion.operator === propertyCriterion.operator && criterion.value === propertyCriterion.value))
            } else {
              delete grantedPrivilege.propertiesCriteria[propertyId]

              if(Object.keys(grantedPrivilege.propertiesCriteria)?.length === 0 ) {
                prev = prev.filter(pri => pri.name !== grantedPrivilege.name);
              }
            }
            // when one property is toggle off, remove "_ALL" privilege property if present
              prev = prev.filter(pri => pri.name !== privilegeAllProperties.name);
              getPrivilegeAscendants(privilege).forEach((asc) => {prev = prev.filter(priv => priv.name !== asc)});
          } else {
            //Property (and criterion) should be - completely - toggled on
            grantedPrivilege.propertiesCriteria = grantedPrivilege.propertiesCriteria || {};
            grantedPrivilege.propertiesCriteria[propertyId] = grantedPrivilege.propertiesCriteria[propertyId] || [];
            if(propertyCriterion) {
              grantedPrivilege.propertiesCriteria[propertyId].push(propertyCriterion);
            } else {
              // when propertyCriterion == null, we try to toggle on/off all criteria of this propertyId
              grantedPrivilege.propertiesCriteria[propertyId] = grantablepropertiesCriteria[propertyId]
            }
          }
        } else {
          // add privilege for this specific propertyId
          let propertiesCriteria = {};
          propertiesCriteria[propertyId] = [];
          if(propertyCriterion) {
            //add privilege with this property
            propertiesCriteria[propertyId] = [propertyCriterion]
          } else if(grantablepropertiesCriteria[propertyId]?.length > 0) {
            //when propertyCriterion == null, we toggle on all criteria of this propertyId
            propertiesCriteria[propertyId] = grantablepropertiesCriteria[propertyId]
          }
          prev.push({ name: privilege.name, propertiesCriteria: propertiesCriteria });
        }
      });
      return Array.from(prev);
    })
  }, [getAllGrantablePropertiesCriteria, hasPrivilegeOnProperty]);


  const grantPrivileges = useCallback((...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        prev.push(privilege);
        getPrivilegeDescendants(privilege).forEach((desc) => prev.push({ name: desc, resources: [] }));
      });
      return Array.from(prev);
    });
  }, [setRolePrivileges]);

  const revokePrivileges = useCallback((...privileges) => {
    setRolePrivileges(prev => {
      privileges.forEach((privilege) => {
        prev = prev.filter(priv => priv.name !== privilege.name);
        getPrivilegeAscendants(privilege).forEach((asc) => prev = prev.filter(priv => priv.name !== asc));
      });
      return Array.from(prev);
    });
  }, [setRolePrivileges]);

  const hasOptionSubscription = useCallback((option) => {
    return hasOption(state?.accounts, state?.account?.id, option)
  }, [state?.account?.id, state?.accounts])

  const rolePrivilegesEditorContext = useMemo(
    () => ({
      isSelectedRoleReadOnly: isSelectedRoleReadOnly
    }),
    [isSelectedRoleReadOnly]
  );

  const handleTabChange = (event, newValue) => setSelectedTab(newValue);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const handleSave = async (shouldInheritBaseRolePrivileges) => {
    try {
      if (submitHandler !== null) {
        submitHandler();
      }

      // Filter privileges for reports
      let privilegesToSave = rolePrivileges;
      const hasReportsAll = rolePrivileges.some(p => p.name === 'PRIVILEGE_VIEW_REPORTS_ALL');

      if (hasReportsAll) {
        // If PRIVILEGE_VIEW_REPORTS_ALL is present, remove PRIVILEGE_VIEW_REPORTS
        privilegesToSave = privilegesToSave.filter(p => p.name !== 'PRIVILEGE_VIEW_REPORTS');
      } else {
        // If PRIVILEGE_VIEW_REPORTS_ALL is not present, check if PRIVILEGE_VIEW_REPORTS has any reports selected
        const reportsPrivilege = privilegesToSave.find(p => p.name === 'PRIVILEGE_VIEW_REPORTS');
        if (reportsPrivilege) {
          const hasSelectedReports = reportsPrivilege.propertiesCriteria &&
                                      Object.keys(reportsPrivilege.propertiesCriteria).length > 0;
          if (!hasSelectedReports) {
            privilegesToSave = privilegesToSave.filter(p => p.name !== 'PRIVILEGE_VIEW_REPORTS');
          }
        }
      }

      await updateRolePrivileges(state.account.id, role.id, { flatPrivileges: privilegesToSave, shouldInheritBaseRolePrivileges: shouldInheritBaseRolePrivileges }, dispatch);
      dispatch({
        type: "ALERT",
        alert: {
          type: SUCCESS,
          msg: "Les modifications ont bien été enregistrées.",
        },
      });

      onValidateSuccess();
    } catch (error) {
      dispatch({
        type: "ALERT",
        alert: {
          type: ERROR,
          msg: "Erreur interne.",
        },
      });
    }finally {
      setIsSavingDialogOpen(false);
    }
  }

  return !role ? (
    <></>
  ) : (
    <RolePrivilegesEditorContext.Provider value={rolePrivilegesEditorContext}>
      <div className={classes.rulesPanel}>
        <div className={classes.rulesPanelHeader}>
          {/* -------------- */}
          {/* --- HEADER --- */}
          {/* -------------- */}
          <EAButton
            onClick={() => setIsSavingDialogOpen(true)}
            content={<EAIcon icon="save" />}
            hoverContent={<EAIcon icon={state.personalization.isCustomTemplate ? "save" : 'save-darkGreen'} />}
            className={classes.saveButton}
            disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_ROLE)}
            customStyle={{ padding: '0 19px', minWidth: '30px' }}
          />
          <Box
            className={classes.tabsWrapper}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="roles tabs"
              centered
              style={{ marginRight: '50px' }}
            >
              <Tab
                label={i18next.t('roles.tabs.configuration')}
                icon={
                  <EAColorIcon
                    icon="roles/configuration"
                    noTransform={selectedTab !== 0}
                    color={theme.palette.primary.main}
                    svgStyle={{ position: "relative", top: "2px", left: "-2px" }}
                  />
                }
                iconPosition="start"
                {...a11yProps(0)}
                className={classes.tab}
              />
              <Tab
                label={i18next.t('roles.tabs.accountScopes')}
                icon={
                  <EAColorIcon
                    icon="roles/accountScopes"
                    noTransform={selectedTab !== 1}
                    color={theme.palette.primary.main}
                    svgStyle={{ position: "relative", top: "2px", left: "-2px" }}
                  />
                }
                iconPosition="start"
                {...a11yProps(1)}
                className={classes.tab}
              />
              <Tab
                label={i18next.t('roles.tabs.features')}
                icon={
                  <EAColorIcon
                    icon="roles/features"
                    noTransform={selectedTab !== 2}
                    color={theme.palette.primary.main}
                    svgStyle={{ position: "relative", top: "2px", left: "-2px" }}
                  />
                }
                iconPosition="start"
                {...a11yProps(2)}
                className={classes.tab}
              />
              <Tab
                label={
                  disabledSourceTab ? (
                    <Tooltip
                      title={i18next.t('roles.tabs.noConfiguredSourceMessage')}
                    >
                      <span>{i18next.t('roles.tabs.sources')}</span>
                    </Tooltip>
                  ) : (
                    <span>{i18next.t('roles.tabs.sources')}</span>
                  )
                }
                style={{ pointerEvents: 'auto' }}
                icon={
                  <EAColorIcon
                    icon="roles/sources"
                    noTransform={selectedTab !== 3}
                    color={theme.palette.primary.main}
                    svgStyle={{ position: "relative", top: "2px", left: "-2px" }}
                  />
                }
                iconPosition="start"
                {...a11yProps(3)}
                className={classes.tab}
              />
              <Tab
                label={i18next.t('roles.tabs.events')}
                icon={
                  <EAColorIcon icon="roles/events"
                          noTransform={selectedTab !== 4}
                          color={theme.palette.primary.main}
                          svgStyle={{ position: "relative", top: "2px", left: "-2px" }}
                  />
                }
                iconPosition="start"
                {...a11yProps(4)}
                className={classes.tab}
              />
            </Tabs>
          </Box>
        </div>

        {/* --------------- */}
        {/* --- CONTENT --- */}
        {/* --------------- */}
        <div className={classes.rulesPanelMainContent}>

          {/* Configuration */}
          <EATabPanel value={selectedTab} index={0} variant={"withoutBackground"}>
            <ConfigurationLine
              title={i18next.t("roles.label.thirdpartyAttributes")}
              actions={THIRD_PARTY_ATTRIBUTE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.dossierAttributes")}
              actions={DOSSIER_ATTRIBUTE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.partnerAttributes")}
              actions={PARTNER_ATTRIBUTE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.smartFlow")}
              actions={RULE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.smartPilot")}
              actions={CALCULATION_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.roles")}
              actions={ROLE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.users")}
              actions={USER_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.label.personalization")}
              actions={THEME_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            {/* <ConfigurationLine
              title={i18next.t("roles.label.consumption")}
              actions={CONSUMPTION_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            /> */}
            <ConfigurationDrop
              title={i18next.t("adminSettings.demandes")}
              actions={REQUEST_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges, hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges)}
              type="dropAction"
            />
            <ConfigurationLine
              title={i18next.t("adminSettings.import")}
              actions={IMPORTS_AND_UPDATES_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationLine
              title={i18next.t("roles.features.context")}
              actions={CONTEXT_MODEL_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
          </EATabPanel>

          {/* Périmètres du compte */}
          <EATabPanel value={selectedTab} index={1} variant={"withoutBackground"}>
            <RoleScopes
              setSubmitHandler={setSubmitHandler}
              onValidateSuccess={onValidateSuccess}
              role={role}
              accountActivities={accountActivities}
              accountCountries={accountCountries}
              accountAttributesAndIndicators={accountAttributesAndIndicators}
              qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
            />
          </EATabPanel>

          {/* Évènements */}
          <EATabPanel value={selectedTab} index={4} variant={"withoutBackground"}>
            <EventPrivilegeBlock
              variant="roles"
              openDefault={true}
              data={TIERS_EVENT_MANAGEMENT(
                hasOptionSubscription,
                togglePrivileges, hasCurrentEventPrivilege, hasBaseAnyOfPrivileges,
                hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges, selectAllEventPrivileges, unselectAllEventPrivileges,
                thirdpartyAttributes,
                thirdpartyCalculatedAttributes,
                thirdpartyIndicators,
                togglePropertyPrivilege,
                togglePrivilegeOnAllProperties,
                hasPrivilegeOnProperty,
                hasCurrentPrivilegeOnAllProperties,
                hasGrantablePrivilegeForProperty,
                getDataHubSources()
                ,
              )}
            />
            <EventPrivilegeBlock
              variant="roles"
              openDefault={true}
              data={DOSSIER_EVENT_MANAGEMENT(togglePrivileges, hasCurrentEventPrivilege, hasBaseAnyOfPrivileges,
                hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges, selectAllEventPrivileges, unselectAllEventPrivileges,
                dossierAttributes,
                dossierCalculatedAttributes,
                dossierIndicators,
                togglePropertyPrivilege,
                togglePrivilegeOnAllProperties,
                hasPrivilegeOnProperty,
                hasCurrentPrivilegeOnAllProperties,
                hasGrantablePrivilegeForProperty,)}
            />
            <EventPrivilegeBlock
              variant="roles"
              openDefault={true}
              data={DOCUMENT_EVENT_MANAGEMENT(togglePrivileges, hasCurrentEventPrivilege, hasBaseAnyOfPrivileges, hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges, selectAllEventPrivileges, unselectAllEventPrivileges)}
            />
          </EATabPanel>

          {/* Fonctionnalités */}
          <EATabPanel value={selectedTab} index={2} variant={"withoutBackground"}>
            <ConfigurationDrop
              title={i18next.t("roles.features.thirdParties")}
              actions={THIRD_PARTY_MANAGEMENT_ACTIONS(
                  togglePrivileges,
                  grantPrivileges,
                  revokePrivileges,
                  hasCurrentAnyOfPrivileges,
                  hasBaseAnyOfPrivileges,
                  thirdpartyAttributes,
                  thirdpartyCalculatedAttributes,
                  thirdpartyIndicators,
                  togglePropertyPrivilege,
                  togglePrivilegeOnAllProperties,
                  hasPrivilegeOnProperty,
                  hasCurrentPrivilegeOnAllProperties,
                  hasGrantablePrivilegeForProperty, )}
              type="dropAction"
            />
            <ConfigurationDrop
              title={i18next.t("roles.features.dossiers")}
              actions={DOSSIER_MANAGEMENT_ACTIONS(
                  togglePrivileges,
                  grantPrivileges,
                  revokePrivileges,
                  hasCurrentAnyOfPrivileges,
                  hasBaseAnyOfPrivileges,
                  dossierAttributes,
                  dossierCalculatedAttributes,
                  dossierIndicators,
                  togglePropertyPrivilege,
                  togglePrivilegeOnAllProperties,
                  hasPrivilegeOnProperty,
                  hasCurrentPrivilegeOnAllProperties,
                  hasGrantablePrivilegeForProperty,)}
              type="dropAction"
            />
            <ConfigurationDrop
              title={i18next.t("roles.features.documents")}
              actions={DOCUMENT_MANAGEMENT_ACTIONS(togglePrivileges, toggleResourcePrivilege, togglePrivilegesForAllResources, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasCurrentAnyOfPrivilegesOnResource, hasCurrentAllOfPrivilegesOnResource, hasBaseAnyOfPrivileges)}
              type="dropActionPerDoc"
              authorizedDocumentTypes={role?.authorizedDocumentTypes}
            />
            <ConfigurationDrop
              title={i18next.t("roles.features.context")}
              actions={CONTEXT_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges)}
              type="dropAction"
            />
            <ConfigurationLine
              title={i18next.t("roles.features.contacts")}
              actions={CONTACT_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
            />
            <ConfigurationDrop
              title={i18next.t("EXPORTS")}
              actions={EXPORTS_MANAGEMENT_ACTIONS(
                  togglePrivileges,
                  hasCurrentAnyOfPrivileges,
                  hasBaseAnyOfPrivileges,
                  accountReports,
                  togglePropertyPrivilege,
                  togglePrivilegeOnAllProperties,
                  hasPrivilegeOnProperty,
                  hasCurrentPrivilegeOnAllProperties,
                  hasGrantablePrivilegeForProperty,
                  rolePrivileges,
                  baseRolePrivileges)}
              type="dropAction"
            />
          </EATabPanel>

          {/* Sources */}
          {hasOption(state.accounts, state.account.id, "indued") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("indued.label")}
                isTitleLowerCase
                translation="false"
                actions={INDUED_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "TRANSPARENCY") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("transparency.label")}
                isTitleLowerCase
                translation="false"
                actions={TRANSPARENCY_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "DATA_HUB") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("thirdpartySheet.dataHub.title")}
                isTitleLowerCase
                translation="false"
                actions={DATA_HUB_MANAGEMENT_ACTIONS(togglePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "FINANCIALSTRENGTH") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("financialStrength.label")}
                isTitleLowerCase
                translation="false"
                actions={FINANCIAL_STRENGTH_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "ECOVADIS") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("ecovadis.EVratings")}
                isTitleLowerCase
                translation="false"
                actions={ECOVADIS_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "OWLIN") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title="Owlin"
                isTitleLowerCase
                translation="false"
                actions={OWLIN_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "ESG") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("ESG.labelScore")}
                isTitleLowerCase
                translation="false"
                actions={DNB_ESG_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "SECURITY_RATING") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("securityRating.label")}
                isTitleLowerCase
                variant="caseSensitive"
                translation="false"
                actions={SECURITY_RATING_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
          {hasOption(state.accounts, state.account.id, "CREDITSAFE") && (
            <EATabPanel value={selectedTab} index={3} variant={"withoutBackground"}>
              <ConfigurationLine
                title={i18next.t("creditsafe.label")}
                isTitleLowerCase
                variant="caseSensitive"
                translation="false"
                actions={CREDITSAFE_MANAGEMENT_ACTIONS(togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges).rootActions}
              />
            </EATabPanel>
          )}
        </div>
      </div>

      {/* PopUp : on create */}
      {isSavingDialogOpen && (
        <RoleSaveDialog
          // baseRoleCandidates={getBaseRoleCandidates(null)}
          isOpen={isSavingDialogOpen}
          onClose={() => setIsSavingDialogOpen(false)}
          // onValidateSuccess={() => {
          //   setIsCreateDialogOpen(false);
          //   fetchRoles();
          // }}
          handleSave={handleSave}
        />
      )}
    </RolePrivilegesEditorContext.Provider>
  );
}

export default PrivilegesPanel;
